import { ArrowLeft, Bookmark, LoaderCircle, ShieldCheck } from "lucide-react";
import { Form, Link, type MetaFunction, useLoaderData } from "react-router";

import { LogoLockup } from "@/site/ui/logo";
import { ThemeControl } from "@/theme/theme-control";
import { Button } from "@/ui/button";
import { useIsPending } from "@/utils/form";

import type { BookmarkedLoginLoaderResponse } from "./login.server";

export const meta: MetaFunction = () => [
  { title: "Sign in with X · Bookmarked FYI" },
];

// Explain identity-only sign-in before redirecting to Bookmarked's X app.
//
export default function BookmarkedLogin() {
  const { error, isXOAuthConfigured } =
    useLoaderData<BookmarkedLoginLoaderResponse>();
  const isPending = useIsPending({ formAction: "/auth/x" });

  return (
    <div className="bookmark-grid flex min-h-dvh items-center justify-center px-5 py-20 sm:px-8">
      <div className="absolute top-5 left-5">
        <LogoLockup linkTo="/" />
      </div>
      <div className="absolute top-5 right-5">
        <ThemeControl />
      </div>

      <main className="bg-card w-full max-w-lg rounded-[2rem] border p-7 shadow-xl sm:p-10">
        <Link
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-2 text-sm"
          to="/"
        >
          <ArrowLeft className="size-4" /> Back home
        </Link>

        <span className="bg-foreground text-background mt-8 flex size-12 items-center justify-center rounded-2xl">
          <Bookmark className="size-5" />
        </span>
        <p className="text-signal-strong mt-6 text-xs font-bold tracking-[0.13em] uppercase">
          Verified curator identity
        </p>
        <h1 className="font-display mt-3 text-4xl font-semibold tracking-[-0.05em] sm:text-5xl">
          Continue with X.
        </h1>
        <p className="text-muted-foreground mt-4 leading-7">
          Your X account proves who controls your Bookmarked handle. Everything
          you save starts private and publishing always requires a separate
          action.
        </p>

        {error && (
          <p
            className="border-destructive/30 bg-destructive/10 text-destructive mt-6 rounded-xl border px-4 py-3 text-sm"
            role="alert"
          >
            {error}
          </p>
        )}

        <Form action="/auth/x" className="mt-7" method="post">
          <Button
            className="h-12 w-full rounded-full text-base"
            disabled={!isXOAuthConfigured || isPending}
          >
            {isPending ? (
              <LoaderCircle className="size-4 animate-spin" />
            ) : (
              <span className="font-display text-lg">X</span>
            )}
            {isPending ? "Connecting to X…" : "Sign in with X"}
          </Button>
        </Form>

        <p className="text-muted-foreground mt-3 text-center text-xs leading-5">
          By continuing, you agree to the{" "}
          <Link className="underline-decoration underline" to="/terms">
            Terms of Service
          </Link>{" "}
          and acknowledge the{" "}
          <Link className="underline-decoration underline" to="/privacy">
            Privacy Policy
          </Link>
          .
        </p>
        <p className="text-muted-foreground mt-2 text-center text-xs">
          Review the available prices, renewal terms, and any trial on the{" "}
          <Link className="underline-decoration underline" to="/pricing">
            Pricing page
          </Link>
          .
        </p>

        {!isXOAuthConfigured && !error && (
          <p className="text-muted-foreground mt-3 text-center text-xs">
            Sign-in is unavailable until this deployment adds its X app keys.
          </p>
        )}

        <div className="text-muted-foreground mt-7 grid gap-3 border-t pt-6 text-sm leading-6">
          <p className="flex items-start gap-2.5">
            <ShieldCheck className="text-success mt-0.5 size-4 shrink-0" />
            Bookmarked stores your stable X user ID and current handle, but not
            an X access token or your private X bookmarks.
          </p>
          <p>
            This first version saves only the public post links you explicitly
            paste into Studio. Your curator plan and payment method are managed
            separately through Stripe Billing.
          </p>
        </div>
      </main>
    </div>
  );
}
